import Component from '../core/Component'
import { toggleClass } from '../utils/dom'
import  debounce from 'lodash/debounce'

function isInTriangle(point, triangle) {
    const p = point
    const { a, b, c } = triangle

    const v0 = [c.x - a.x, c.y - a.y]
    const v1 = [b.x - a.x, b.y - a.y]
    const v2 = [p.x - a.x, p.y - a.y]

    const dot00 = (v0[0] * v0[0]) + (v0[1] * v0[1])
    const dot01 = (v0[0] * v1[0]) + (v0[1] * v1[1])
    const dot02 = (v0[0] * v2[0]) + (v0[1] * v2[1])
    const dot11 = (v1[0] * v1[0]) + (v1[1] * v1[1])
    const dot12 = (v1[0] * v2[0]) + (v1[1] * v2[1])

    const invDenom = 1 / (dot00 * dot11 - dot01 * dot01)

    const u = (dot11 * dot02 - dot01 * dot12) * invDenom
    const v = (dot00 * dot12 - dot01 * dot02) * invDenom

    return ((u >= 0) && (v >= 0) && (u + v < 1))
}

export default class Navigation extends Component {
    constructor(element) {
        super(element)

        this.ref = {
            links: [],
            items: [],
            list: null,
            nav: null,
            subnav: null,
            subnavInner: null
        }

        this.timer = null


        this.itemsBox = null
        this.triangle = {
            a: {
                x: 0,
                y: 0
            },
            b: {
                x: 0,
                y: 0
            },
            c: {
                x: 0,
                y: 0
            }
        }

        this._state = {
            active: -1,
            next: -1,
            open: false
        }

        this.paddingHeight = 0
        this.handleResize = debounce(this.handleResize, 200)

    }

    prepare() {
        document.addEventListener('swup:animationOutStart', this.handleSwupAnimationOutStart)
        window.addEventListener('resize', this.handleResize)

        if (!this.ref.list) {
            return
        }

        this.ref.list.addEventListener('mouseenter', this.handleListMouseEnter)
        this.ref.list.addEventListener('mouseleave', this.handleListMouseLeave)

        this.ref.links.forEach((item, index) => {
            item.addEventListener('mouseenter', event => {
                this.setState({
                    next: index,
                    active: this.state.next > -1 ? this.state.active : index,
                    open: true
                })
            })
            item.addEventListener('mouseleave', event => {
                this.setState({
                    open: false
                })
            })
        })

        this.ref.subnav.addEventListener('mouseenter', event => {
            this.setState({
                open: true
            })
        })

        this.ref.subnav.addEventListener('mouseleave', event => {
            this.setState({
                open: false
            })
        })

        this.resize()
    }

    destroy() {
        clearTimeout(this.timer)
        document.removeEventListener('swup:animationOutStart', this.handleSwupAnimationOutStart)
        window.removeEventListener('resize', this.handleResize)
    }

    handleResize = event => {
        this.resize()
    }

    handleSwupAnimationOutStart = event => {
        this.setState({
            open: false
        })
    }

    handleListMouseEnter = event => {
        this.itemBox = this.ref.items[0].getBoundingClientRect()

        this.triangle.a = {
            x: event.clientX,
            y: event.clientY
        }

        this.triangle.b = {
            x: this.itemBox.left,
            y: this.itemBox.top
        }

        this.triangle.c = {
            x: this.itemBox.left + this.itemBox.width,
            y: this.itemBox.top
        }

        this.ref.list.addEventListener('mousemove', this.handleListMouseMove)
    }

    handleListMouseLeave = event => {
        this.ref.list.removeEventListener('mousemove', this.handleListMouseMove)
    }

    handleListMouseMove = event => {
        const isInside = isInTriangle({
            x: event.clientX,
            y: event.clientY
        }, this.triangle)

        if (!isInside) {
            this.triangle.a.x = event.clientX
            this.triangle.a.y = event.clientY

            if (this.state.next !== this.state.active) {
                this.setState({
                    active: this.state.next,
                    direction: this.getDirection(this.state.next)
                })
            }
        }
    }

    resize() {
        let height = 0

        height += parseInt(window.getComputedStyle(this.ref.subnavInner, null).getPropertyValue('padding-top'))
        height += parseInt(window.getComputedStyle(this.ref.subnavInner, null).getPropertyValue('padding-bottom'))

        this.paddingHeight = height
        this.checkListScroll()
    }

    open() {
        if (this.state.active >= 0) {
            this.ref.subnav.classList.add('is-active')
            this.ref.links[this.state.active].classList.add('is-active')
            clearTimeout(this.timer)
        }
    }

    close() {
        this.ref.links.forEach(item => item.classList.remove('is-active'))
        this.ref.subnav.classList.remove('is-active')

        this.timer = setTimeout(() => {
            this.setState({
               active: -1,
               next: -1
            })
        }, 100)
    }

    activate = index => {
        this.ref.items.forEach((element, i) => toggleClass(element, 'is-active', i === index))
        this.ref.links.forEach((element, i) => toggleClass(element, 'is-active', i === index))

        if (this.ref.items[index]) {
            this.ref.subnavInner.style.height = `${this.ref.items[index].scrollHeight + this.paddingHeight}px`
        }

        this.applyDirection()
    }

    getDirection(index) {
        let direction = 'bottom'

        if (this.state.active > -1) {
            if (this.state.active < index) {
                direction = 'left'
            } else if (this.state.active > index) {
                direction = 'right'
            }
        }

        return direction
    }

    applyDirection() {
        this.ref.subnav.classList.add('from-' + this.state.direction)
        setTimeout(() => {
            ['bottom', 'right', 'left', 'none'].forEach(item => {
                this.ref.subnav.classList.remove('from-' + item)
            })
        }, 40)
    }

    stateChange(changes) {
        if ('active' in changes) {
            this.activate(changes.active)
            this.applyDirection()
        }

        if ('open' in changes) {
            if (this.state.open) {
                this.open()
            } else {
                this.close()
            }
        }
    }



    checkListScroll() {
        if (this.ref.list.scrollWidth > this.ref.list.offsetWidth) {
            this.attachListScroll()
        } else {
            this.detachListScroll()
        }
    }

    attachListScroll() {
        if (this.ref.list.classList.contains('is-overflowing')) {
            return
        }

        this.ref.list.classList.add('is-overflowing')
        this.ref.list.addEventListener('scroll', this.handleListScroll)
    }

    detachListScroll() {
        this.ref.list.classList.remove('is-overflowing')
        this.ref.list.removeEventListener('scroll', this.handleListScroll)
    }

    handleListScroll = event => {
        if (this.ref.list.scrollWidth - this.ref.list.offsetWidth === this.ref.list.scrollLeft) {
            this.ref.list.classList.add('is-overflowing-end')
        } else {
            this.ref.list.classList.remove('is-overflowing-end')
        }
    }
}
